import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { settingsActions } from "../store/index.js";

const roles = {
  CLIENT: "Client",
  ADMIN: "Admin",
};

const AuthContext = createContext();

export const getUserRoleFromToken = (token) => {
  if (!token) return "0";

  try {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) throw new Error("Invalid token format");

    const decodedPayload = JSON.parse(atob(tokenParts[1])); // Decode payload

    return decodedPayload.role || null;
  } catch (error) {
    return "0";
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() =>
    window.localStorage.getItem("token")
  );
  const [tokenRole, setTokenRole] = useState("annonymus");
  const userId = useSelector((state) => state.settings.userId);
  const navigate = useNavigate();
  const dispach = useDispatch();

  // call this function when you want to authenticate the user
  const loginSucces = useCallback(
    async (userToken, role, id) => {
      //verify if role is correct
      const userRole = getUserRoleFromToken(userToken);

      setTokenRole(userRole);
      if (role && role !== userRole) {
        console.error("User role does not match the required role.");
        alert("Nu aveti permisiunea de a va conecta!");
        return;
      } else {
        setToken(userToken);
        window.localStorage.setItem("token", userToken);
        dispach(settingsActions.logIn(id));
        if (role === roles.CLIENT) navigate("/account");
        else if (role === roles.ADMIN) navigate("backoffice");
      }
    },
    [dispach, navigate]
  );

  // call this function to sign out logged in user after token expiration
  const logout = useCallback(() => {
    navigate("/", { replace: true });
    setTimeout(() => {
      setToken(null);
      window.localStorage.removeItem("token");
      dispach(settingsActions.logOut());
      setTokenRole("annonymus");
    }, 50);
  }, [dispach, navigate]);

  useEffect(() => {
    if (token) {
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length !== 3) {
          throw new Error("Invalid token format");
        }

        const decodedToken = JSON.parse(atob(tokenParts[1])); // Decodează payload-ul JWT

        if (!decodedToken.exp) {
          throw new Error("Token does not contain an expiration date");
        }

        const expirationTime = decodedToken.exp * 1000; // Transformă în milisecunde
        const remainingTime = expirationTime - Date.now();

        if (remainingTime <= 0) {
          logout();
        } else {
          setTimeout(() => {
            logout();
          }, remainingTime);
        }
        setTokenRole(decodedToken.role);
      } catch (error) {
        console.error("Error decoding token:", error);
        logout();
      }
    }
  }, [logout, token]);

  const value = useMemo(
    () => ({
      token,
      tokenRole,
      userId,
      loginSucces,
      logout,
    }),
    [token, userId, loginSucces, logout, tokenRole]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (role) => {
  return useContext(AuthContext);
};
